import { getActiveTenantId } from '../store/tenantStore';

const getScopedKey = (key: string, tenantId?: string): string => {
  const activeTenant = tenantId || getActiveTenantId();
  return `${activeTenant}:${key}`;
};

export const getInternalToken = (tenantId?: string) => {
  const token = localStorage.getItem(getScopedKey('internal_token', tenantId)) || localStorage.getItem('internal_token');
  const timestamp = localStorage.getItem(getScopedKey('token_validated_at', tenantId)) || localStorage.getItem('token_validated_at');
  if (!token || !timestamp) return null;

  const isValid = Date.now() - parseInt(timestamp) < 60 * 60 * 1000; // 1 hour
  return isValid ? token : null;
};

// Function to decode JWT token and extract user data
export const decodeJWTToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

export const resolveTenantFromToken = (token: string): string | null => {
  const decoded = decodeJWTToken(token);
  const iss = String(decoded?.iss || '').toLowerCase().trim();
  if (!iss) return null;

  if (iss.includes('curvelms.uk') || iss.includes('curvelearning')) {
    return 'curvelearning';
  }
  if (iss.includes('palletways')) {
    return 'palletways';
  }
  if (iss.includes('nourish')) {
    return 'nourish';
  }
  if (iss.includes('redcrier')) {
    return 'redcrier';
  }
  if (iss.includes('myako')) {
    return 'myako';
  }
  return null;
};

const ensureAbsoluteURL = (url: string): string => {
  if (!url || !url.trim()) return url;
  
  const trimmed = url.trim();
  
  if (trimmed.match(/^https?:\/\//i)) {
    return trimmed;
  }
  
  if (trimmed.startsWith('/')) {
    return trimmed;
  }
  
  return `https://${trimmed}`;
};

export const setInternalToken = (
  token: string, 
  role: string, 
  organisationId: string, 
  originalMyakoToken?: string, 
  redirectURL?: string,
  tenantId?: string
) => {
  const scopedTenant = tenantId || getActiveTenantId();

  localStorage.setItem(getScopedKey('user_role', scopedTenant), role);
  localStorage.setItem(getScopedKey('internal_token', scopedTenant), token);
  localStorage.setItem(getScopedKey('user_organisation_id', scopedTenant), organisationId);
  localStorage.setItem(getScopedKey('token_validated_at', scopedTenant), Date.now().toString());

  // Also sync to legacy un-scoped keys for backward compatibility
  localStorage.setItem('user_role', role);
  localStorage.setItem('internal_token', token);
  localStorage.setItem('user_organisation_id', organisationId);
  localStorage.setItem('token_validated_at', Date.now().toString());
  
  // Store the original Myako token if provided
  if (originalMyakoToken) {
    localStorage.setItem(getScopedKey('original_myako_token', scopedTenant), originalMyakoToken);
    localStorage.setItem('original_myako_token', originalMyakoToken);
  }
  
  if (redirectURL) {
    const normalizedURL = ensureAbsoluteURL(redirectURL);
    localStorage.setItem(getScopedKey('redirect_url', scopedTenant), normalizedURL);
    localStorage.setItem('redirect_url', normalizedURL);
  }
  
  // Extract and store user email from JWT token
  const decodedToken = decodeJWTToken(token);
  if (decodedToken?.email) {
    localStorage.setItem(getScopedKey('user_email', scopedTenant), decodedToken.email);
    localStorage.setItem('user_email', decodedToken.email);
  }
};

export const clearInternalToken = (tenantId?: string) => {
  const scopedTenant = tenantId || getActiveTenantId();

  localStorage.removeItem(getScopedKey('user_role', scopedTenant));
  localStorage.removeItem(getScopedKey('internal_token', scopedTenant));
  localStorage.removeItem(getScopedKey('token_validated_at', scopedTenant));
  localStorage.removeItem(getScopedKey('user_organisation_id', scopedTenant));
  localStorage.removeItem(getScopedKey('user_email', scopedTenant));
  localStorage.removeItem(getScopedKey('original_myako_token', scopedTenant));

  localStorage.removeItem('user_role');
  localStorage.removeItem('internal_token');
  localStorage.removeItem('token_validated_at');
  localStorage.removeItem('user_organisation_id');
  localStorage.removeItem('user_email');
  localStorage.removeItem('original_myako_token');
  localStorage.removeItem('active_tenant_id');
};

export const getUserRole = (tenantId?: string) => {
  return localStorage.getItem(getScopedKey('user_role', tenantId)) || localStorage.getItem('user_role');
};

export const getUserOrganisationId = (tenantId?: string) => {
  return localStorage.getItem(getScopedKey('user_organisation_id', tenantId)) || localStorage.getItem('user_organisation_id');
};

export const getUserEmail = (tenantId?: string) => {
  return localStorage.getItem(getScopedKey('user_email', tenantId)) || localStorage.getItem('user_email');
};

export const getOriginalMyakoToken = (tenantId?: string) => {
  return localStorage.getItem(getScopedKey('original_myako_token', tenantId)) || localStorage.getItem('original_myako_token');
};

export const isSameMyakoToken = (newToken: string, tenantId?: string) => {
  const storedToken = getOriginalMyakoToken(tenantId);
  return storedToken === newToken;
};

export const getRedirectURL = (tenantId?: string) => {
  const url = localStorage.getItem(getScopedKey('redirect_url', tenantId)) || localStorage.getItem('redirect_url');
  
  if (url) {
    const absoluteURL = ensureAbsoluteURL(url);
    return absoluteURL;
  }
  
  return url;
};

export const setRedirectURL = (redirectURL: string, tenantId?: string) => {
  const url = ensureAbsoluteURL(redirectURL);
  localStorage.setItem(getScopedKey('redirect_url', tenantId), url);
  localStorage.setItem('redirect_url', url);
};

export const clearRedirectURL = (tenantId?: string) => {
  localStorage.removeItem(getScopedKey('redirect_url', tenantId));
  localStorage.removeItem('redirect_url');
};

// Function to manually refresh token using stored external token
export const refreshInternalToken = async (tenantId?: string): Promise<boolean> => {
  try {
    const externalToken = getOriginalMyakoToken(tenantId);
    if (!externalToken) {
      return false;
    }

    const authAPIsService = (await import('../hooks/authAPIs')).default;
    const refreshData = await authAPIsService.validateMyakoToken(externalToken, tenantId);
    
    // Use redirect_url from backend response if available, otherwise use existing one
    const redirectURL = refreshData.redirect_url || getRedirectURL(tenantId);
    if (refreshData.redirect_url) {
      setRedirectURL(refreshData.redirect_url, tenantId);
    }
    
    setInternalToken(
      refreshData.internalToken,
      refreshData.role,
      refreshData.organisationId,
      externalToken,
      redirectURL || undefined,
      tenantId
    );
    
    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return false;
  }
};